/**
 * GPR national shipping (Spec 2). Loaded only when settings.enable_national_shipping is on
 * (snippets/gpr-fulfillment-head.liquid).
 *
 * Contract: a Ship-capable product has an option named `Fulfillment` with the values
 * `Pickup at the Ranch` / `Ship to me` (matched case-insensitively).
 */
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

export const PICKUP_VALUE = 'Pickup at the Ranch';
export const SHIP_VALUE = 'Ship to me';

/** @param {unknown} value */
export const normalizeFulfillment = (value) => String(value ?? '').trim().toLowerCase();

/**
 * @param {{ options?: string[] } | null} variant
 * @returns {'pickup' | 'ship' | ''}
 */
export function sideOfVariant(variant) {
  const options = (variant?.options || []).map(normalizeFulfillment);
  if (options.includes(normalizeFulfillment(SHIP_VALUE))) return 'ship';
  if (options.includes(normalizeFulfillment(PICKUP_VALUE))) return 'pickup';
  return '';
}

/* T3.9 · the Pickup / Ship line-property toggle lives in gpr-line-properties.js (loaded without the flag). */

/* ------------------------------------------------------------------ */
/* Shopper preference store (T3.13 builds the waterfall on this)        */
/* ------------------------------------------------------------------ */

const PREF_KEY = 'gpr:fulfillment-preference';
/** @type {ReadonlyArray<string>} */
export const PREFERENCES = ['ship', 'pickup', 'both'];

/** @returns {string} the resolved preference: ship | pickup | both */
export function getPreference() {
  const value = document.documentElement.dataset.gprPref;
  return value && PREFERENCES.includes(value) ? value : 'pickup';
}

/**
 * Records a choice made in this browser (waterfall level 1) and tells Klaviyo when the
 * profile is identified. The theme never reads Klaviyo.
 * @param {string} preference
 */
export function setPreference(preference) {
  if (!PREFERENCES.includes(preference)) return;
  try {
    localStorage.setItem(PREF_KEY, preference);
  } catch (_e) {
    // Storage blocked: the choice still applies to this page view.
  }
  const root = document.documentElement;
  const changed = root.dataset.gprPref !== preference;
  root.dataset.gprPref = preference;
  root.dataset.gprPrefSource = 'browser';
  if (changed) document.dispatchEvent(new CustomEvent('gpr:preference', { detail: { preference } }));
  identifyPreference(preference);
}

/** @param {string} preference */
function identifyPreference(preference) {
  const klaviyo = /** @type {any} */ (window).klaviyo;
  if (!klaviyo?.isIdentified) return;
  Promise.resolve(klaviyo.isIdentified())
    .then((identified) => {
      if (identified) klaviyo.identify({ fulfillment_preference: preference });
    })
    .catch(() => {});
}

/* ------------------------------------------------------------------ */
/* T3.8 · PDP fulfillment selector                                      */
/* ------------------------------------------------------------------ */

class FulfillmentSelector extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const section = this.closest('.shopify-section, dialog') || document;

    this.addEventListener('change', this.#onChange, { signal });
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.#hideSingleValueRow();
    if (this.dataset.mode === 'cards') this.#applyToCards(this.dataset.selectedSide || 'pickup');
    this.#applyPreferenceDefault();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get section() {
    return this.closest('.shopify-section, dialog') || document;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.dataset.side) return;
    event.stopPropagation();
    const side = input.dataset.side;
    this.#selectSide(side);
    // Choosing a side updates the shopper preference. A "both" shopper stays "both".
    if (getPreference() !== 'both') setPreference(side);
  };

  /**
   * Selects the side in Horizon's variant picker, which fetches the variant and fires variant:update.
   * @param {string} side
   */
  #selectSide(side) {
    if (this.dataset.mode === 'cards') {
      this.#applyToCards(side);
      return true;
    }
    const want = normalizeFulfillment(side === 'ship' ? SHIP_VALUE : PICKUP_VALUE);
    const picker = this.section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!picker) return false;
    const radio = [...picker.querySelectorAll('input[type="radio"]')].find(
      (input) => input instanceof HTMLInputElement && normalizeFulfillment(input.value) === want
    );
    if (radio instanceof HTMLInputElement) {
      if (!radio.checked) radio.click();
      return true;
    }
    const select = picker.querySelector('select[name="options[Fulfillment]"]');
    const match = select instanceof HTMLSelectElement
      ? [...select.options].find((o) => normalizeFulfillment(o.value) === want)
      : null;
    if (select instanceof HTMLSelectElement && match && select.value !== match.value) {
      select.value = match.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  /** @param {Event} event */
  #onVariantUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    const html = detail?.data?.html;
    if (!html) return;
    const fresh = html.querySelector(`gpr-fulfillment-selector[data-block-id="${this.dataset.blockId}"]`);
    if (fresh) {
      morph(this, fresh);
      // morph() updates children only; carry the root state across so CSS keyed on it (Add to cart colour) follows.
      for (const key of ['state', 'selectedSide', 'pickupAvailable', 'shipAvailable']) {
        if (fresh.dataset[key] !== undefined) this.dataset[key] = fresh.dataset[key];
      }
    }
    this.#hideSingleValueRow();
  };

  /**
   * PDP default by preference (spec: ship → Ship; pickup / both → Pickup, the first variant).
   * Only when the URL doesn't already pick a variant, and never onto a sold-out side.
   */
  #applyPreferenceDefault() {
    if (this.dataset.state !== 'both') return;
    if (new URL(window.location.href).searchParams.has('variant')) return;
    const preference = getPreference();
    const want = preference === 'ship' ? 'ship' : 'pickup';
    if (this.dataset.selectedSide === want) return;
    if (this.dataset[`${want}Available`] !== 'true') return;
    this.#selectSide(want);
  }

  /**
   * Cards mode (product.parent): switch every purchase-options card in this section to the
   * side's variant, price and line property. A card with no variant, or a sold-out one, on
   * that side can't be added and says why. Cards without a Fulfillment option count as pickup-only.
   * @param {string} side
   */
  #applyToCards(side) {
    this.dataset.selectedSide = side;
    this.querySelectorAll('.gpr-fs__input').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = input.dataset.side === side;
    });
    const texts = {
      soldOut: side === 'ship' ? this.dataset.textShipSoldOut : this.dataset.textPickupSoldOut,
      missing: side === 'ship' ? this.dataset.textPickupOnly : this.dataset.textShipOnly,
    };
    this.section.querySelectorAll('.purchase-option').forEach((card) => {
      const script = card.querySelector('script[data-gpr-card-variants]');
      /** @type {{ id: number, available: boolean, price: string } | null} */
      let variant = null;
      if (script) {
        variant = JSON.parse(script.textContent || '{}')[side] || null;
      } else if (side === 'pickup') {
        return; // no Fulfillment option: a pickup item, as today
      }
      const form = card.querySelector('form');
      const idInput = form?.querySelector('input[name="id"]');
      const addButton = card.querySelector('.purchase-option__add-button');
      const accelerated = card.querySelector('.purchase-option__accelerated');
      const price = card.querySelector('.purchase-option__price');
      let note = card.querySelector('.gpr-card-note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'gpr-card-note';
        card.querySelector('.purchase-option__buttons')?.append(note);
      }
      const buyable = Boolean(variant && variant.available);
      if (variant && idInput instanceof HTMLInputElement) idInput.value = String(variant.id);
      if (variant && price) price.textContent = variant.price;
      if (addButton instanceof HTMLButtonElement) addButton.disabled = !buyable;
      if (accelerated instanceof HTMLElement) accelerated.hidden = !buyable;
      if (note instanceof HTMLElement) {
        note.hidden = buyable;
        note.textContent = buyable ? '' : (variant ? texts.soldOut : texts.missing) || '';
      }
      form?.querySelectorAll('input[data-gpr-side]').forEach((input) => {
        if (input instanceof HTMLInputElement) input.disabled = input.dataset.gprSide !== side;
      });
    });
  }

  /** Horizon renders a one-value option as text, not an input; hide that row by its name. */
  #hideSingleValueRow() {
    const picker = this.section.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    picker?.querySelectorAll('[data-testid="variant-option-single"]').forEach((row) => {
      if (row instanceof HTMLElement && row.firstChild?.textContent?.trim() === 'Fulfillment') row.hidden = true;
    });
  }
}

if (!customElements.get('gpr-fulfillment-selector')) {
  customElements.define('gpr-fulfillment-selector', FulfillmentSelector);
}

/* ------------------------------------------------------------------ */
/* T3.12 · Mixed-cart guard (snippets/cart-fulfillment-guard.liquid)    */
/* ------------------------------------------------------------------ */

const SAVED_KEY = 'gpr:saved-lines';

/** @returns {Array<{ id: number, quantity: number, properties: Object, side: string }>} */
function readSavedLines() {
  try {
    const lines = JSON.parse(localStorage.getItem(SAVED_KEY) || '[]');
    return Array.isArray(lines) ? lines : [];
  } catch (_e) {
    return [];
  }
}

/** @param {Array<Object>} lines */
function writeSavedLines(lines) {
  try {
    if (lines.length) localStorage.setItem(SAVED_KEY, JSON.stringify(lines));
    else localStorage.removeItem(SAVED_KEY);
  } catch (_e) {}
}

/** @param {string} url @param {Object} body */
async function postJSON(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} ${response.status}`);
  return response.json();
}

class CartGuard extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#orderButtons();
    this.#renderSaved();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** The button for the shopper's preferred side goes first (spec: the guard prefers their lines). */
  #orderButtons() {
    const preference = getPreference();
    const preferred = this.querySelector(`[data-keep="${preference}"]`);
    if (preferred && preferred.parentElement) preferred.parentElement.prepend(preferred);
  }

  #renderSaved() {
    const box = this.querySelector('[data-gpr-saved]');
    const text = this.querySelector('[data-gpr-saved-text]');
    if (!(box instanceof HTMLElement) || !text) return;
    const count = readSavedLines().reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    box.hidden = count === 0 || this.dataset.mixed === 'true';
    text.textContent = (this.dataset.savedText || '').replace('[count]', String(count));
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const keep = target?.closest('[data-keep]');
    const restore = target?.closest('[data-gpr-restore]');
    if (!keep && !restore) return;
    event.preventDefault();
    const button = /** @type {HTMLButtonElement} */ (keep || restore);
    button.disabled = true;
    try {
      if (keep instanceof HTMLElement) await this.#split(keep.dataset.keep || 'pickup');
      else await this.#restore();
      window.location.reload();
    } catch (error) {
      console.error(error);
      button.disabled = false;
    }
  };

  /**
   * Keeps one side in the cart and sets the other aside in this browser.
   * @param {string} keepSide
   */
  async #split(keepSide) {
    const script = this.querySelector('script[data-gpr-lines]');
    /** @type {Array<{ side: string, key: string, id: number, quantity: number, properties: Object }>} */
    const lines = JSON.parse(script?.textContent || '[]');
    const moving = lines.filter((line) => line.side !== keepSide);
    if (!moving.length) return;
    /** @type {Record<string, number>} */
    const updates = {};
    moving.forEach((line) => (updates[line.key] = 0));
    await postJSON(this.dataset.updateUrl || '/cart/update.js', { updates });
    const saved = readSavedLines();
    moving.forEach(({ id, quantity, properties, side }) => saved.push({ id, quantity, properties, side }));
    writeSavedLines(saved);
  }

  async #restore() {
    const saved = readSavedLines();
    if (!saved.length) return;
    // Sep 29 fix: the cart JSON gives properties as [[name, value], ...]; /cart/add.js needs
    // { name: value } and answered 400, so "Add them to my cart" silently did nothing.
    const asObject = (p) => (Array.isArray(p) ? Object.fromEntries(p) : p || {});
    const items = saved.map(({ id, quantity, properties }) => ({ id, quantity, properties: asObject(properties) }));
    await postJSON(this.dataset.addUrl || '/cart/add.js', { items });
    writeSavedLines([]);
  }
}

if (!customElements.get('gpr-cart-guard')) {
  customElements.define('gpr-cart-guard', CartGuard);
}

// Checkout stays blocked while the cart is mixed (the buttons are also dimmed by the guard's CSS).
document.addEventListener(
  'submit',
  (event) => {
    const submitter = /** @type {SubmitEvent} */ (event).submitter;
    if (submitter?.getAttribute('name') !== 'checkout') return;
    if (!document.querySelector('gpr-cart-guard[data-mixed="true"]')) return;
    event.preventDefault();
    document.querySelector('gpr-cart-guard[data-mixed="true"] [role="alert"]')?.scrollIntoView({ block: 'center' });
  },
  true
);

/* ------------------------------------------------------------------ */
/* T3.13 · Where shoppers choose: header chip, prompt, section, PDP     */
/* ------------------------------------------------------------------ */

const PROMPT_DISMISSED_KEY = 'gpr:pref-prompt-dismissed';

function syncChoiceButtons() {
  const preference = getPreference();
  document.querySelectorAll('[data-gpr-pref-choices] [data-gpr-pref]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.getAttribute('data-gpr-pref') === preference));
  });
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  const choice = target?.closest('[data-gpr-pref-choices] [data-gpr-pref]');
  if (choice) {
    setPreference(choice.getAttribute('data-gpr-pref') || '');
    dismissPrompt();
    const popover = choice.closest('[popover]');
    if (popover instanceof HTMLElement && 'hidePopover' in popover) popover.hidePopover();
    return;
  }
  if (target?.closest('[data-gpr-prompt-close]')) dismissPrompt();
});

document.addEventListener('gpr:preference', syncChoiceButtons);

function dismissPrompt() {
  try {
    localStorage.setItem(PROMPT_DISMISSED_KEY, '1');
  } catch (_e) {}
  document.querySelector('.gpr-pref-prompt')?.remove();
}

/** Unknown visitors (waterfall level 4) get the prompt once. */
function maybeShowPrompt() {
  if (document.documentElement.dataset.gprPrefSource !== 'default') return;
  try {
    if (localStorage.getItem(PROMPT_DISMISSED_KEY)) return;
  } catch (_e) {
    return;
  }
  if (window.Shopify?.designMode) return;
  const template = document.getElementById('GprPrefPromptTemplate');
  if (!(template instanceof HTMLTemplateElement)) return;
  document.body.append(template.content.cloneNode(true));
  syncChoiceButtons();
}

/* Collections: ship shoppers open collections filtered to Ship to me (snippets/collection-ship-filter). */
const SHIP_FILTER_OFF_KEY = 'gpr:ship-filter-off:';

class ShipFilter extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-gpr-ship-filter-off]')) return;
      try {
        sessionStorage.setItem(SHIP_FILTER_OFF_KEY + this.dataset.collection, '1');
      } catch (_e) {}
    });
    this.#applyDefault();
  }

  #applyDefault() {
    if (getPreference() !== 'ship') return;
    if (this.dataset.default !== 'true' || this.dataset.active === 'true' || !this.dataset.addUrl) return;
    try {
      if (sessionStorage.getItem(SHIP_FILTER_OFF_KEY + this.dataset.collection)) return;
    } catch (_e) {}
    // Only on a collection opened without filters of its own.
    const params = new URL(window.location.href).searchParams;
    if ([...params.keys()].some((key) => key.startsWith('filter.'))) return;
    window.location.replace(this.dataset.addUrl);
  }
}

if (!customElements.get('gpr-ship-filter')) {
  customElements.define('gpr-ship-filter', ShipFilter);
}

syncChoiceButtons();
maybeShowPrompt();

/* ------------------------------------------------------------------ */
/* T3.15 · Grids follow the shopper's preference (hide, don't dim)     */
/* ------------------------------------------------------------------ */
/*
 * Cards carry <span hidden data-gpr-fit="pickup|ship|both"> (snippets/shippable-badge).
 * Ship shoppers don't see pickup-only cards; pickup shoppers don't see ship-only cards;
 * "both" shoppers and visitors who haven't chosen see everything. Each section gets a
 * pill to see everything on this page ("See all"), which doesn't change the preference.
 * Fixed sections (home page, recommendations) that would drop below two cards show in
 * full instead. Collection and search grids never do, so the pill stays the way back.
 */
const FIT_MIN_VISIBLE = 2;
const FIT_WORDS = { ship: 'plants we ship', pickup: 'pickup at the Ranch' };
/** Section ids where the shopper chose See all on this page view. */
const fitSeeAll = new Set();

/** The preference to filter by: unknown visitors (no choice yet) see everything. */
function filterPreference() {
  if (document.documentElement.dataset.gprPrefSource === 'default') return 'both';
  return getPreference();
}

/** @param {string} fit @param {string} preference */
function fitsPreference(fit, preference) {
  return preference === 'both' || fit === 'both' || fit === preference;
}

/** @param {Element} marker */
function fitItem(marker) {
  const card = marker.closest('product-card, .product-card');
  if (!card) return null;
  // In carousels hide the whole slide, or its empty frame keeps the space.
  return card.closest('slideshow-slide') || card.closest('.product-grid__item, .resource-list__item, li') || card;
}

/**
 * @param {Element} section
 * @param {Element} firstItem
 * @param {string} state - '' (no pill), 'filtered' or 'all'
 * @param {string} preference
 */
function renderFitPill(section, firstItem, state, preference) {
  let pill = section.querySelector(':scope .gpr-fit-pill');
  if (!state) {
    pill?.remove();
    return;
  }
  if (!pill) {
    pill = document.createElement('p');
    pill.className = 'gpr-fit-pill';
    // Lower right, under the cards. Home/product-list sections are a page-width grid whose
    // carousel runs full bleed, so the pill goes in the content column after the last list.
    const grid = firstItem.closest('.section-resource-list');
    const gridLists = grid ? grid.querySelectorAll(':scope > .resource-list') : [];
    if (gridLists.length) {
      pill.classList.add('gpr-fit-pill--grid');
      gridLists[gridLists.length - 1].after(pill);
    } else {
      const list = firstItem.closest('.product-grid, .resource-list, slideshow-component') || firstItem.parentElement;
      list?.after(pill);
    }
  }
  const words = FIT_WORDS[preference] || '';
  pill.innerHTML = '';
  const label = document.createElement('span');
  label.textContent = state === 'filtered' ? `Showing ${words} · ` : 'Showing everything · ';
  const toggle = document.createElement('button');
  toggle.type = 'button';
  toggle.className = 'gpr-fit-pill__toggle';
  toggle.textContent = state === 'filtered' ? 'See all' : `Only ${words}`;
  toggle.addEventListener('click', () => {
    if (state === 'filtered') fitSeeAll.add(section.id);
    else fitSeeAll.delete(section.id);
    applyFitFilter();
  });
  pill.append(label, toggle);
}

function applyFitFilter() {
  const preference = filterPreference();
  /** @type {Map<Element, Array<{ item: Element, fit: string }>>} */
  const sections = new Map();
  document.querySelectorAll('main [data-gpr-fit]').forEach((marker) => {
    const item = fitItem(marker);
    const section = item?.closest('.shopify-section');
    if (!item || !section) return;
    if (!sections.has(section)) sections.set(section, []);
    sections.get(section)?.push({ item, fit: marker.getAttribute('data-gpr-fit') || 'pickup' });
  });

  sections.forEach((entries, section) => {
    const mismatched = entries.filter((entry) => !fitsPreference(entry.fit, preference)).length;
    const isGrid = Boolean(section.querySelector('.product-grid__item'));
    const tooFew = !isGrid && entries.length - mismatched < FIT_MIN_VISIBLE;
    const seeAll = fitSeeAll.has(section.id);
    const hide = preference !== 'both' && mismatched > 0 && !tooFew && !seeAll;

    entries.forEach((entry) => {
      entry.item.classList.toggle('gpr-fit-hidden', hide && !fitsPreference(entry.fit, preference));
    });

    let state = '';
    if (preference !== 'both' && mismatched > 0 && !tooFew) state = seeAll ? 'all' : 'filtered';
    renderFitPill(section, entries[0].item, state, preference);
    refreshCarousels(entries);
  });
}

/**
 * Horizon only re-checks carousel arrows when the scroller resizes, so after hiding or
 * showing slides, hide the arrows when everything left fits, and scroll back to the start.
 * @param {Array<{ item: Element }>} entries
 */
function refreshCarousels(entries) {
  const carousels = new Set();
  entries.forEach(({ item }) => {
    const carousel = item.matches('slideshow-slide') ? item.closest('slideshow-component') : null;
    if (carousel) carousels.add(carousel);
  });
  if (!carousels.size) return;
  requestAnimationFrame(() => {
    carousels.forEach((carousel) => {
      // @ts-ignore Horizon components expose their refs
      const { scroller, next } = carousel.refs || {};
      if (!(scroller instanceof HTMLElement)) return;
      scroller.scrollLeft = 0;
      const arrows = next instanceof HTMLElement ? next.closest('slideshow-arrows') : null;
      arrows?.classList.toggle('gpr-fit-hidden', scroller.scrollWidth <= scroller.offsetWidth + 1);
    });
  });
}

let fitQueued = false;
function queueFitFilter() {
  if (fitQueued) return;
  fitQueued = true;
  requestAnimationFrame(() => {
    fitQueued = false;
    applyFitFilter();
  });
}

document.addEventListener('gpr:preference', () => {
  fitSeeAll.clear();
  queueFitFilter();
});

// Infinite scroll, variant re-renders and section loads add cards later.
const fitMain = document.querySelector('main');
if (fitMain) {
  new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element && (node.matches('[data-gpr-fit]') || node.querySelector('[data-gpr-fit]'))) {
          queueFitFilter();
          return;
        }
      }
    }
  }).observe(fitMain, { childList: true, subtree: true });
}

applyFitFilter();
